import React, { createContext, useContext, ReactNode, useState, useCallback, useEffect } from 'react';
import MobileChartOverlay from '../components/charts/MobileChartOverlay';

type ChartType = 'verascore' | 'dti' | 'cash' | 'spending' | 'savings' | 'debt' | 'payment' | 'credit' | 'custom';

interface MobileChartContextType {
  isOpen: boolean;
  isMobile: boolean;
  activeChart: ChartType | null;
  chartTitle: string;
  openChart: (chartType: ChartType, title: string, content: ReactNode) => void;
  closeChart: () => void;
}

const MobileChartContext = createContext<MobileChartContextType | undefined>(undefined);

interface MobileChartProviderProps {
  children: ReactNode;
}

export const MobileChartProvider: React.FC<MobileChartProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [activeChart, setActiveChart] = useState<ChartType | null>(null);
  const [chartTitle, setChartTitle] = useState('');
  const [chartContent, setChartContent] = useState<ReactNode | null>(null);
  
  // Check if device is mobile on mount and resize
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Initial check
    checkIfMobile();
    
    // Add event listener for window resize
    window.addEventListener('resize', checkIfMobile);
    
    // Clean up
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);
  
  // Function to open chart overlay
  const openChart = useCallback((chartType: ChartType, title: string, content: ReactNode) => {
    setActiveChart(chartType);
    setChartTitle(title);
    setChartContent(content);
    setIsOpen(true);
  }, []);
  
  // Function to close chart overlay
  const closeChart = useCallback(() => {
    setIsOpen(false);
    // Don't clear content immediately to prevent UI flicker
    setTimeout(() => {
      setActiveChart(null);
      setChartContent(null);
    }, 300);
  }, []);
  
  return (
    <MobileChartContext.Provider 
      value={{ 
        isOpen, 
        isMobile, 
        activeChart, 
        chartTitle, 
        openChart, 
        closeChart 
      }}
    >
      {children}
      
      {/* Mobile Chart Overlay */}
      <MobileChartOverlay
        isOpen={isOpen}
        onClose={closeChart}
        title={chartTitle}
      >
        {chartContent}
      </MobileChartOverlay>
    </MobileChartContext.Provider>
  );
};

// Custom hook to use the mobile chart context
export const useMobileChart = () => {
  const context = useContext(MobileChartContext);
  if (context === undefined) {
    throw new Error('useMobileChart must be used within a MobileChartProvider');
  }
  return context;
}; 